import { Link } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Mail, ArrowLeft } from 'lucide-react';
import SEO from '../components/SEO';

const TermsOfService = () => {
  return (
    <>
      <SEO 
        title="Terms of Service"
        description="Terms of Service for Instant-Email. Read our terms and conditions for using our temporary disposable email service."
        path="/terms"
      />
      
      <div className="min-h-screen bg-background">
        {/* Navigation */}
        <nav className="fixed top-0 left-0 right-0 z-50 glass">
          <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2">
              <Mail className="w-8 h-8 text-primary" />
              <span className="font-heading text-xl font-semibold text-foreground">Instant-Email</span>
            </Link>
            <Link to="/">
              <Button variant="ghost" className="text-foreground hover:text-primary">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Home
              </Button>
            </Link>
          </div>
        </nav>

        {/* Content */}
        <div className="pt-28 pb-16 px-6">
          <div className="max-w-4xl mx-auto">
            <h1 className="font-heading text-4xl font-bold text-foreground mb-4">Terms of Service</h1>
            <p className="text-muted-foreground mb-8">Last updated: January 3, 2026</p>

            <Card className="bg-card border-border">
              <CardContent className="p-8 prose prose-slate dark:prose-invert max-w-none">
                <section className="mb-8">
                  <h2 className="text-2xl font-semibold mb-4">1. Acceptance of Terms</h2>
                  <p>
                    By accessing and using Instant-Email ("the Service"), you accept and agree to be bound by the 
                    terms and provision of this agreement. If you do not agree to these Terms of Service, please 
                    do not use the Service.
                  </p>
                </section>

                <section className="mb-8">
                  <h2 className="text-2xl font-semibold mb-4">2. Description of Service</h2>
                  <p>
                    Instant-Email provides temporary, disposable email addresses that can be used to receive emails. 
                    The Service includes:
                  </p>
                  <ul>
                    <li>Generation of temporary email addresses</li>
                    <li>Reception and display of incoming emails</li>
                    <li>Automatic deletion of emails after 24 hours</li>
                    <li>Optional user accounts for managing multiple temporary addresses</li>
                  </ul>
                </section>

                <section className="mb-8">
                  <h2 className="text-2xl font-semibold mb-4">3. User Responsibilities</h2>
                  <p>As a user of the Service, you agree to:</p>
                  <ul>
                    <li>Use the Service only for lawful purposes</li>
                    <li>Not use the Service to receive illegal content</li>
                    <li>Not use the Service to spam, harass, or harm others</li>
                    <li>Not attempt to gain unauthorized access to our systems</li>
                    <li>Not use the Service to create accounts on services that prohibit temporary email addresses</li>
                    <li>Maintain the confidentiality of your account credentials (if registered)</li>
                  </ul>
                </section>

                <section className="mb-8">
                  <h2 className="text-2xl font-semibold mb-4">4. Prohibited Uses</h2>
                  <p>You may not use the Service to:</p>
                  <ul>
                    <li>Violate any local, state, national, or international law</li>
                    <li>Receive or distribute illegal content, including child exploitation material</li>
                    <li>Receive emails related to illegal activities or fraud</li>
                    <li>Circumvent security features or authentication systems</li>
                    <li>Distribute malware, viruses, or harmful code</li>
                    <li>Impersonate any person or entity</li>
                    <li>Interfere with or disrupt the Service or servers</li>
                  </ul>
                </section>

                <section className="mb-8">
                  <h2 className="text-2xl font-semibold mb-4">5. Service Availability</h2>
                  <p>
                    We strive to provide uninterrupted service, but we do not guarantee that the Service will be 
                    available at all times. We reserve the right to modify, suspend, or discontinue the Service 
                    (or any part thereof) at any time with or without notice.
                  </p>
                </section>

                <section className="mb-8">
                  <h2 className="text-2xl font-semibold mb-4">6. Data and Privacy</h2>
                  <p>
                    All temporary email addresses and messages are automatically deleted after 24 hours. We do not 
                    guarantee the security or privacy of emails received through the Service. Please refer to our 
                    <Link to="/privacy" className="text-primary hover:underline ml-1">Privacy Policy</Link> for 
                    more information about how we handle your data.
                  </p>
                </section>

                <section className="mb-8">
                  <h2 className="text-2xl font-semibold mb-4">7. Intellectual Property</h2>
                  <p>
                    The Service and its original content, features, and functionality are owned by Instant-Email 
                    and are protected by international copyright, trademark, patent, trade secret, and other 
                    intellectual property laws.
                  </p>
                </section>

                <section className="mb-8">
                  <h2 className="text-2xl font-semibold mb-4">8. Disclaimer of Warranties</h2>
                  <p>
                    THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EITHER 
                    EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO IMPLIED WARRANTIES OF MERCHANTABILITY, 
                    FITNESS FOR A PARTICULAR PURPOSE, OR NON-INFRINGEMENT.
                  </p>
                  <p className="mt-4">
                    We do not warrant that:
                  </p>
                  <ul>
                    <li>The Service will be uninterrupted, timely, secure, or error-free</li>
                    <li>The results obtained from using the Service will be accurate or reliable</li>
                    <li>Any errors in the Service will be corrected</li>
                  </ul>
                </section>

                <section className="mb-8">
                  <h2 className="text-2xl font-semibold mb-4">9. Limitation of Liability</h2>
                  <p>
                    TO THE MAXIMUM EXTENT PERMITTED BY LAW, IN NO EVENT SHALL INSTANT-EMAIL BE LIABLE FOR ANY 
                    INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING WITHOUT LIMITATION, 
                    LOSS OF PROFITS, DATA, USE, OR OTHER INTANGIBLE LOSSES, RESULTING FROM:
                  </p>
                  <ul>
                    <li>Your access to or use of (or inability to access or use) the Service</li>
                    <li>Any conduct or content of any third party on the Service</li>
                    <li>Unauthorized access, use, or alteration of your transmissions or content</li>
                  </ul>
                </section>

                <section className="mb-8">
                  <h2 className="text-2xl font-semibold mb-4">10. Indemnification</h2>
                  <p>
                    You agree to indemnify, defend, and hold harmless Instant-Email and its officers, directors, 
                    employees, and agents from and against any and all claims, damages, obligations, losses, 
                    liabilities, costs, or debt, and expenses arising from your use of the Service or violation 
                    of these Terms.
                  </p>
                </section>

                <section className="mb-8">
                  <h2 className="text-2xl font-semibold mb-4">11. Termination</h2>
                  <p>
                    We may terminate or suspend your account and access to the Service immediately, without prior 
                    notice or liability, for any reason, including if you breach these Terms of Service.
                  </p>
                </section>

                <section className="mb-8">
                  <h2 className="text-2xl font-semibold mb-4">12. Governing Law</h2>
                  <p>
                    These Terms shall be governed by and construed in accordance with the laws of the jurisdiction 
                    in which Instant-Email operates, without regard to its conflict of law provisions.
                  </p>
                </section>

                <section className="mb-8">
                  <h2 className="text-2xl font-semibold mb-4">13. Changes to Terms</h2>
                  <p>
                    We reserve the right to modify or replace these Terms at any time. If a revision is material, 
                    we will provide at least 30 days' notice prior to any new terms taking effect. What constitutes 
                    a material change will be determined at our sole discretion.
                  </p>
                </section>

                <section className="mb-8">
                  <h2 className="text-2xl font-semibold mb-4">14. Severability</h2>
                  <p>
                    If any provision of these Terms is held to be unenforceable or invalid, such provision will be 
                    changed and interpreted to accomplish the objectives of such provision to the greatest extent 
                    possible under applicable law, and the remaining provisions will continue in full force and effect.
                  </p>
                </section>

                <section className="mb-8">
                  <h2 className="text-2xl font-semibold mb-4">15. Contact Information</h2>
                  <p>
                    If you have any questions about these Terms, please contact us at:
                  </p>
                  <p className="mt-4">
                    <strong>Email:</strong> legal@instant-email.org<br />
                    <strong>Website:</strong> <a href="https://instant-email.org" className="text-primary hover:underline">instant-email.org</a>
                  </p>
                </section>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </>
  );
};

export default TermsOfService;
